import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  TableColumnType,
  message,
  Select,
  Table,
  Tag,
  Drawer,
  Space,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  ResourceMaterial,
  MaterialStatus,
  MaterialType,
  getResourceMaterialPageList,
  upResourceMaterial,
  downResourceMaterial,
} from '@/api/resourceMaterial';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import MaterialForm from './components/CreateForm';

const ResourceMaterialPage = () => {
  const isMobile = useAtomValue(isMobileAtom);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const [createFormVisible, setCreateFormVisible] = useState(false);

  const [form] = Form.useForm<{
    status: MaterialStatus;
  }>();

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = isMobile ? 10 : 20;

  const {
    data,
    isLoading,
    refetch: refetchResourceMaterialList,
  } = useQuery({
    queryKey: ['resource-material-list', currentPage, code],
    queryFn: async () => {
      const values = form.getFieldsValue();
      const params = {
        currentPage,
        pageSize,
        ...values,
      };

      if (!code) {
        return Promise.reject(new Error('缺少资源位编码'));
      }

      return getResourceMaterialPageList(code, params);
    },
    enabled: !!code,
  });

  const handleSearch = () => {
    setCurrentPage(1);
    refetchResourceMaterialList();
  };

  const handleReset = () => {
    form.resetFields();
    setCurrentPage(1);
    refetchResourceMaterialList();
  };

  const handleUp = async (id: number) => {
    try {
      await upResourceMaterial(code!, id);
      messageApi.success('上架成功');
      refetchResourceMaterialList();
    } catch (error: any) {
      messageApi.error(error.message || '上架失败');
    }
  };

  const handleDown = async (id: number) => {
    try {
      await downResourceMaterial(code!, id);
      messageApi.success('下架成功');
      refetchResourceMaterialList();
    } catch (error: any) {
      messageApi.error(error.message || '下架失败');
    }
  };

  const columns = useMemo<TableColumnType<ResourceMaterial>[]>(
    () => [
      {
        title: '资源位ID',
        dataIndex: 'code',
        key: 'code',
      },
      {
        title: '资源位位置',
        dataIndex: 'codeName',
        key: 'codeName',
      },
      {
        title: '素材图',
        key: 'images',
        render: (record: ResourceMaterial) => {
          const images = record.positionMaterialImageResourceDTOList || [];
          return (
            <div style={{ display: 'flex', gap: '8px' }}>
              {images.map((item, index) => (
                <div
                  key={index}
                  style={{
                    width: record.materialType === MaterialType.Single ? '200px' : '120px',
                    height: record.materialType === MaterialType.Single ? '48px' : '60px',
                  }}
                >
                  <img
                    src={item.imageUrl}
                    alt={`素材图${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              ))}
            </div>
          );
        },
      },
      {
        title: '素材描述',
        dataIndex: 'materialDesc',
        key: 'materialDesc',
        width: 200,
        ellipsis: true,
      },
      {
        title: '素材类型',
        dataIndex: 'materialType',
        key: 'materialType',
        render: (type: MaterialType) => (
          <Tag color={type === MaterialType.Single ? 'blue' : 'purple'}>
            {type === MaterialType.Single ? '单图' : '双图'}
          </Tag>
        ),
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        render: (status: MaterialStatus) => (
          <Tag color={status === MaterialStatus.Online ? 'success' : 'default'}>
            {status === MaterialStatus.Online ? '上线' : '下线'}
          </Tag>
        ),
      },
      {
        title: '排序',
        dataIndex: 'sort',
        key: 'sort',
      },
      {
        title: '开始时间',
        dataIndex: 'startTime',
        key: 'startTime',
        render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
      },
      {
        title: '结束时间',
        dataIndex: 'endTime',
        key: 'endTime',
        render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
      },
      {
        title: '创建人',
        dataIndex: 'createUserName',
        key: 'createUserName',
      },
      {
        title: '操作',
        key: 'action',
        render: (_, record) => (
          <Row gutter={8}>
            <Col>
              <Button
                type="link"
                onClick={() => navigate(`/resource-material/detail?code=${code}&id=${record.id}`)}
              >
                详情
              </Button>
            </Col>
            <Col>
              {record.status === MaterialStatus.Online ? (
                <Button type="link" onClick={() => handleDown(record.id)}>
                  下架
                </Button>
              ) : (
                <Button type="link" onClick={() => handleUp(record.id)}>
                  上架
                </Button>
              )}
            </Col>
          </Row>
        ),
      },
    ],
    []
  );

  const mobileColumns = useMemo<TableColumnType<ResourceMaterial>[]>(
    () => [
      {
        title: '素材图',
        key: 'images',
        render: (record: ResourceMaterial) => {
          const images = record.positionMaterialImageResourceDTOList || [];
          return (
            <div style={{ display: 'flex', gap: '4px' }}>
              {images.map((item, index) => (
                <div
                  key={index}
                  style={{
                    width: record.materialType === MaterialType.Single ? '150px' : '80px',
                    height: record.materialType === MaterialType.Single ? '36px' : '40px',
                  }}
                >
                  <img
                    src={item.imageUrl}
                    alt={`素材图${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              ))}
            </div>
          );
        },
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        render: (status: MaterialStatus) => (
          <Tag color={status === MaterialStatus.Online ? 'success' : 'default'}>
            {status === MaterialStatus.Online ? '上线' : '下线'}
          </Tag>
        ),
      },
      {
        title: '操作',
        key: 'action',
        render: (_, record) => (
          <Space>
            <Button
              type="link"
              size="small"
              onClick={() => navigate(`/resource-material/detail?code=${code}&id=${record.id}`)}
            >
              详情
            </Button>
            {record.status === MaterialStatus.Online ? (
              <Button type="link" size="small" onClick={() => handleDown(record.id)}>
                下架
              </Button>
            ) : (
              <Button type="link" size="small" onClick={() => handleUp(record.id)}>
                上架
              </Button>
            )}
          </Space>
        ),
      },
    ],
    []
  );

  return (
    <div className="resource-material-page" style={{ padding: isMobile ? '8px' : '16px' }}>
      {contextHolder}
      <div className="search-form" style={{ marginBottom: isMobile ? 8 : 16 }}>
        <Form 
          form={form} 
          layout={isMobile ? "vertical" : "inline"}
          style={{ width: '100%' }}
        >
          <Form.Item name="status" label="状态" style={{ marginBottom: isMobile ? 8 : 16 }}>
            <Select
              placeholder="请选择状态"
              style={{ width: isMobile ? '100%' : 120 }}
              allowClear
              options={[
                { label: '上线', value: MaterialStatus.Online },
                { label: '下线', value: MaterialStatus.Offline },
              ]}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: isMobile ? 8 : 16 }}>
            <Space wrap>
              <Button type="primary" onClick={handleSearch}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      <div style={{ marginBottom: isMobile ? 8 : 16 }}>
        <Button type="primary" onClick={() => setCreateFormVisible(true)}>
          创建素材
        </Button>
      </div>

      <Table
        columns={isMobile ? mobileColumns : columns}
        dataSource={data?.data.records}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: isMobile ? 'max-content' : undefined }}
        pagination={{
          current: currentPage,
          pageSize,
          total: data?.data.total,
          onChange: setCurrentPage,
          size: isMobile ? 'small' : 'default',
        }}
        size={isMobile ? 'small' : 'middle'}
      />

      <MaterialForm
        open={createFormVisible}
        onCancel={() => setCreateFormVisible(false)}
        onSuccess={() => {
          setCreateFormVisible(false);
          refetchResourceMaterialList();
        }}
        resourceCode={code!}
        mode="create"
      />
    </div>
  );
};

export default ResourceMaterialPage;
